/**
 * Push Notifications & Reminders Utility
 * Full-featured notification system with Service Worker support
 * 
 * Features:
 * - Push notification subscription management
 * - Service Worker based persistent notifications
 * - Scheduled reminders with background support
 * - Local fallback when push is unavailable
 */

export interface Reminder {
  id: string;
  title: string;
  body: string;
  time: string; // ISO string
  createdAt: string;
  type?: string;
  recurring?: 'once' | 'daily' | 'weekly' | 'monthly';
}

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

const STORAGE_KEY = 'dipps_scheduled_reminders';
const PUSH_SUBSCRIPTION_KEY = 'dipps_push_subscription';

// Application Server Public Key (VAPID) - this is a demo key
// For production, generate your own at https://vapidkeys.com/
const VAPID_PUBLIC_KEY = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';

/**
 * Convert URL-safe base64 to Uint8Array for VAPID key
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Check if push notifications are supported
 */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Request notification permission from the browser
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }

  return Notification.permission;
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPush(): Promise<PushSubscription | null> {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported');
    return null;
  }

  try {
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      return null;
    }

    const registration = await navigator.serviceWorker.ready;
    
    // Check for existing subscription
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      // Create new subscription
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      
      console.log('[Push] New subscription created:', subscription.endpoint);
    }

    // Store subscription locally
    const subscriptionData: PushSubscriptionData = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))),
        auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!))),
      },
    };
    localStorage.setItem(PUSH_SUBSCRIPTION_KEY, JSON.stringify(subscriptionData));

    return subscription;
  } catch (error) {
    console.error('[Push] Failed to subscribe:', error);
    return null;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      localStorage.removeItem(PUSH_SUBSCRIPTION_KEY);
      console.log('[Push] Unsubscribed successfully');
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('[Push] Failed to unsubscribe:', error);
    return false;
  }
}

/**
 * Get current push subscription status
 */
export async function getPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Get all stored reminders
 */
export function getReminders(): Reminder[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return [];
    }
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to parse reminders:', error);
    return [];
  }
}

/**
 * Save reminders to storage
 */
export function saveReminders(reminders: Reminder[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(reminders));
}

/**
 * Schedule a reminder notification using Service Worker
 */
export async function scheduleReminder(
  title: string,
  body: string,
  time: string | Date,
  options?: { type?: string; recurring?: 'once' | 'daily' | 'weekly' | 'monthly' }
): Promise<{ success: boolean; reminder?: Reminder; error?: string }> {
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    return {
      success: false,
      error: 'Notification permission not granted. Enable notifications for reminders!',
    };
  }

  const scheduledTime = typeof time === 'string' ? new Date(time) : time;
  const now = new Date();
  const delay = scheduledTime.getTime() - now.getTime();

  if (delay < 0) {
    return {
      success: false,
      error: 'Cannot schedule a reminder in the past',
    };
  }

  // Create reminder object
  const reminder: Reminder = {
    id: `reminder_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    title,
    body,
    time: scheduledTime.toISOString(),
    createdAt: now.toISOString(),
    type: options?.type,
    recurring: options?.recurring || 'once',
  };

  // Schedule using Service Worker if available
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Use the Periodic Background Sync API if available (Chrome only)
      // Otherwise fall back to setTimeout with Service Worker notification
      
      // Store the scheduled notification data
      const scheduledNotifications = JSON.parse(
        localStorage.getItem('dipps_scheduled_notifications') || '[]'
      );
      scheduledNotifications.push({
        id: reminder.id,
        title,
        body,
        scheduledTime: scheduledTime.getTime(),
        type: options?.type,
      });
      localStorage.setItem('dipps_scheduled_notifications', JSON.stringify(scheduledNotifications));

      // Schedule with setTimeout (will work while tab is open)
      if (delay <= 2147483647) {
        setTimeout(async () => {
          await showServiceWorkerNotification(registration, title, body, {
            tag: reminder.id,
            data: { url: '/reminders', type: options?.type },
          });
          
          // Handle recurring reminders
          if (options?.recurring && options.recurring !== 'once') {
            scheduleNextRecurrence(reminder);
          }
        }, delay);
      }
    } catch (error) {
      console.warn('[Notification] Service Worker scheduling failed, using fallback:', error);
      // Fallback to basic setTimeout
      if (delay <= 2147483647) {
        setTimeout(() => {
          showNotification(title, body);
        }, delay);
      }
    }
  } else {
    // Fallback for browsers without Service Worker
    if (delay <= 2147483647) {
      setTimeout(() => {
        showNotification(title, body);
      }, delay);
    }
  }

  // Save to reminders list
  const reminders = getReminders();
  reminders.push(reminder);
  saveReminders(reminders);

  return { success: true, reminder };
}

/**
 * Schedule next occurrence for recurring reminders
 */
function scheduleNextRecurrence(reminder: Reminder): void {
  const currentTime = new Date(reminder.time);
  let nextTime: Date;

  switch (reminder.recurring) {
    case 'daily':
      nextTime = new Date(currentTime.getTime() + 24 * 60 * 60 * 1000);
      break;
    case 'weekly':
      nextTime = new Date(currentTime.getTime() + 7 * 24 * 60 * 60 * 1000);
      break;
    case 'monthly':
      nextTime = new Date(currentTime);
      nextTime.setMonth(nextTime.getMonth() + 1);
      break;
    default:
      return;
  }

  // Schedule the next occurrence
  scheduleReminder(reminder.title, reminder.body, nextTime, {
    type: reminder.type,
    recurring: reminder.recurring,
  });
}

/**
 * Show notification using Service Worker
 */
export async function showServiceWorkerNotification(
  registration: ServiceWorkerRegistration,
  title: string,
  body: string,
  options?: NotificationOptions & { data?: any }
): Promise<void> {
  const notificationOptions: NotificationOptions = {
    body,
    icon: '/icons/icon-192x192.png',
    badge: '/icons/icon-72x72.png',
    vibrate: [200, 100, 200],
    tag: options?.tag || `notification_${Date.now()}`,
    requireInteraction: true,
    actions: [
      { action: 'open', title: 'Open' },
      { action: 'dismiss', title: 'Dismiss' },
    ],
    data: options?.data || { url: '/' },
    ...options,
  };

  try {
    await registration.showNotification(title, notificationOptions);
    console.log('[Notification] Shown via Service Worker:', title);
    
    // Play sound
    playNotificationSound();
  } catch (error) {
    console.error('[Notification] Service Worker notification failed:', error);
    // Fallback to regular notification
    showNotification(title, body);
  }
}

/**
 * Show a notification immediately (fallback method)
 */
export function showNotification(title: string, body: string): void {
  if (Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: `reminder_${Date.now()}`,
      requireInteraction: true,
    });

    playNotificationSound();
  }
}

/**
 * Play notification sound
 */
function playNotificationSound(): void {
  try {
    // Try to play a built-in sound or use Web Audio API
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    // Create a pleasant notification beep
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
    
    // Second beep
    setTimeout(() => {
      const oscillator2 = audioContext.createOscillator();
      const gainNode2 = audioContext.createGain();
      
      oscillator2.connect(gainNode2);
      gainNode2.connect(audioContext.destination);
      
      oscillator2.frequency.setValueAtTime(1000, audioContext.currentTime);
      oscillator2.type = 'sine';
      
      gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator2.start(audioContext.currentTime);
      oscillator2.stop(audioContext.currentTime + 0.3);
    }, 200);
  } catch {
    // Audio not available or blocked
  }
}

/**
 * Remove a reminder by ID
 */
export function removeReminder(id: string): void {
  const reminders = getReminders();
  const filtered = reminders.filter((r) => r.id !== id);
  saveReminders(filtered);
  
  // Also remove from scheduled notifications
  try {
    const scheduled = JSON.parse(localStorage.getItem('dipps_scheduled_notifications') || '[]');
    const filteredScheduled = scheduled.filter((n: any) => n.id !== id);
    localStorage.setItem('dipps_scheduled_notifications', JSON.stringify(filteredScheduled));
  } catch {
    // Ignore errors
  }
}

/**
 * Clear all reminders
 */
export function clearAllReminders(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem('dipps_scheduled_notifications');
}

/**
 * Check and trigger any due notifications (for page visibility changes)
 */
export async function checkDueNotifications(): Promise<void> {
  try {
    const scheduled = JSON.parse(localStorage.getItem('dipps_scheduled_notifications') || '[]');
    const now = Date.now();
    const due = scheduled.filter((n: any) => n.scheduledTime <= now);
    const pending = scheduled.filter((n: any) => n.scheduledTime > now);
    
    if (due.length > 0) {
      const registration = await navigator.serviceWorker?.ready;
      
      for (const notification of due) {
        if (registration) {
          await showServiceWorkerNotification(registration, notification.title, notification.body, {
            tag: notification.id,
            data: { url: '/reminders', type: notification.type },
          });
        } else {
          showNotification(notification.title, notification.body);
        }
      }
      
      // Update storage with remaining notifications
      localStorage.setItem('dipps_scheduled_notifications', JSON.stringify(pending));
    }
  } catch (error) {
    console.error('[Notification] Error checking due notifications:', error);
  }
}

/**
 * Initialize notification system
 */
export async function initializeNotifications(): Promise<{
  supported: boolean;
  permission: NotificationPermission;
  pushSubscription: PushSubscription | null;
}> {
  const supported = isPushSupported();
  const permission = Notification.permission;
  let pushSubscription: PushSubscription | null = null;
  
  if (supported && permission === 'granted') {
    pushSubscription = await getPushSubscription();
    
    // Check for any due notifications when app loads
    checkDueNotifications();
    
    // Also check when page becomes visible
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkDueNotifications();
      }
    });
  }
  
  return { supported, permission, pushSubscription };
}

// Export types for use in components
export type { NotificationPermission };
